(() => {
class MotSocialProofGallery {
  constructor(gallery) {
    this.gallery = gallery;
    this.rail = gallery.querySelector('[data-mot-social-rail]');
    this.triggers = Array.from(gallery.querySelectorAll('[data-mot-social-trigger]'));
    this.previousButton = gallery.querySelector('[data-mot-social-previous]');
    this.nextButton = gallery.querySelector('[data-mot-social-next]');
    this.lightbox = gallery.querySelector('[data-mot-social-lightbox]');
    this.lightboxPanel = gallery.querySelector('[data-mot-social-lightbox-panel]');
    this.viewer = gallery.querySelector('[data-mot-social-viewer]');
    this.slides = Array.from(gallery.querySelectorAll('[data-mot-social-slide]'));
    this.modalPrevious = gallery.querySelector('[data-mot-social-modal-previous]');
    this.modalNext = gallery.querySelector('[data-mot-social-modal-next]');
    this.closeButton = gallery.querySelector('[data-mot-social-close]');
    this.activeIndex = 0;
    this.lastTrigger = null;
    this.scrollTimer = null;
    this.controlPositionTimer = null;

    if (!this.rail || !this.lightbox || !this.viewer || this.slides.length === 0) return;

    // Keep the modal outside the product layout's stacking and containment contexts
    // so its backdrop covers the header and the full viewport.
    if (this.lightbox.parentElement !== document.body) {
      document.body.appendChild(this.lightbox);
    }

    this.bindEvents();
    this.updateRailButtons();
  }

  bindEvents() {
    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => this.open(index, trigger));
    });

    this.previousButton?.addEventListener('click', () => this.scrollRail(-1));
    this.nextButton?.addEventListener('click', () => this.scrollRail(1));
    this.rail.addEventListener('scroll', () => this.updateRailButtons(), { passive: true });

    this.modalPrevious?.addEventListener('click', () => this.showSlide(this.activeIndex - 1));
    this.modalNext?.addEventListener('click', () => this.showSlide(this.activeIndex + 1));
    this.closeButton?.addEventListener('click', () => this.close());

    this.viewer.addEventListener('scroll', () => {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => this.syncActiveSlide(), 80);
    }, { passive: true });

    this.lightbox.addEventListener('click', (event) => {
      if (event.target === this.lightbox) this.close();
    });

    this.lightbox.addEventListener('keydown', (event) => this.handleKeydown(event));

    window.addEventListener('resize', () => this.scheduleViewerControls());
    this.slides.forEach((slide) => {
      const media = slide.querySelector('img, video');
      media?.addEventListener(media.tagName === 'VIDEO' ? 'loadedmetadata' : 'load', () => {
        this.scheduleViewerControls();
      });
    });
  }

  scrollRail(direction) {
    const firstCard = this.triggers[0];
    const cardWidth = firstCard?.getBoundingClientRect().width || this.rail.clientWidth / 4;
    const gap = Number.parseFloat(getComputedStyle(this.gallery).getPropertyValue('--mot-social-gap')) || 12;

    this.rail.scrollBy({
      left: direction * (cardWidth + gap) * 4,
      behavior: 'smooth'
    });
  }

  updateRailButtons() {
    if (!this.previousButton || !this.nextButton) return;

    this.previousButton.disabled = this.rail.scrollLeft <= 2;
    this.nextButton.disabled = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - 2;
  }

  open(index, trigger) {
    this.lastTrigger = trigger;
    this.lightbox.hidden = false;
    document.body.classList.add('mot-social-proof-open');
    this.showSlide(index, false);
    this.scheduleViewerControls();
    this.closeButton?.focus();
  }

  close() {
    this.pauseVideos();
    this.lightbox.hidden = true;
    document.body.classList.remove('mot-social-proof-open');
    this.lastTrigger?.focus();
  }

  showSlide(index, smooth = true) {
    this.activeIndex = (index + this.slides.length) % this.slides.length;
    this.viewer.scrollTo({
      left: this.viewer.clientWidth * this.activeIndex,
      behavior: smooth ? 'smooth' : 'auto'
    });
    this.updateViewerState();
    this.scheduleViewerControls(smooth ? 350 : 0);
  }

  syncActiveSlide() {
    if (this.viewer.clientWidth === 0) return;

    this.activeIndex = Math.max(
      0,
      Math.min(this.slides.length - 1, Math.round(this.viewer.scrollLeft / this.viewer.clientWidth))
    );
    this.updateViewerState();
    this.scheduleViewerControls();
  }

  updateViewerState() {
    this.pauseVideos(this.activeIndex);

    const activeVideo = this.slides[this.activeIndex]?.querySelector('video');
    activeVideo?.play().catch(() => {});

  }

  scheduleViewerControls(delay = 0) {
    window.clearTimeout(this.controlPositionTimer);
    this.controlPositionTimer = window.setTimeout(() => {
      window.requestAnimationFrame(() => this.positionViewerControls());
    }, delay);
  }

  positionViewerControls() {
    if (this.lightbox.hidden || !this.lightboxPanel) return;

    const activeMedia = this.slides[this.activeIndex]?.querySelector('img, video');
    if (!activeMedia) return;

    const panelRect = this.lightboxPanel.getBoundingClientRect();
    const mediaRect = this.getVisibleMediaRect(activeMedia);
    if (mediaRect.width === 0 || mediaRect.height === 0) return;

    const mediaTop = Math.max(0, mediaRect.top - panelRect.top);
    const mediaLeft = Math.max(0, mediaRect.left - panelRect.left);
    const mediaRight = Math.max(0, panelRect.right - mediaRect.right);
    const mediaCentreY = mediaTop + mediaRect.height / 2;

    this.lightboxPanel.style.setProperty('--mot-social-media-top', `${mediaTop}px`);
    this.lightboxPanel.style.setProperty('--mot-social-media-left', `${mediaLeft}px`);
    this.lightboxPanel.style.setProperty('--mot-social-media-right', `${mediaRight}px`);
    this.lightboxPanel.style.setProperty('--mot-social-media-centre-y', `${mediaCentreY}px`);
  }

  getVisibleMediaRect(media) {
    const elementRect = media.getBoundingClientRect();
    const intrinsicWidth = media.tagName === 'VIDEO' ? media.videoWidth : media.naturalWidth;
    const intrinsicHeight = media.tagName === 'VIDEO' ? media.videoHeight : media.naturalHeight;
    const objectFit = getComputedStyle(media).objectFit;

    if (!intrinsicWidth || !intrinsicHeight || objectFit !== 'contain') return elementRect;

    const scale = Math.min(elementRect.width / intrinsicWidth, elementRect.height / intrinsicHeight);
    const width = intrinsicWidth * scale;
    const height = intrinsicHeight * scale;

    return {
      left: elementRect.left + (elementRect.width - width) / 2,
      right: elementRect.right - (elementRect.width - width) / 2,
      top: elementRect.top + (elementRect.height - height) / 2,
      bottom: elementRect.bottom - (elementRect.height - height) / 2,
      width,
      height
    };
  }

  pauseVideos(exceptIndex = -1) {
    this.slides.forEach((slide, index) => {
      if (index === exceptIndex) return;
      slide.querySelectorAll('video').forEach((video) => video.pause());
    });
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showSlide(this.activeIndex - 1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showSlide(this.activeIndex + 1);
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      this.lightbox.querySelectorAll('button:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])')
    ).filter((element) => !element.hidden && element.offsetParent !== null);

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

const initialiseMotSocialProofGalleries = (scope = document) => {
  scope.querySelectorAll('[data-mot-social-proof]:not([data-mot-social-ready])').forEach((gallery) => {
    gallery.dataset.motSocialReady = 'true';
    new MotSocialProofGallery(gallery);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initialiseMotSocialProofGalleries());
} else {
  initialiseMotSocialProofGalleries();
}

document.addEventListener('shopify:section:load', (event) => initialiseMotSocialProofGalleries(event.target));
})();
